import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  isValid,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from 'date-fns';

export type RangePreset =
  | 'today'
  | 'yesterday'
  | 'last_7_days'
  | 'this_week'
  | 'last_week'
  | 'last_30_days'
  | 'this_month'
  | 'last_month'
  | 'all'
  | 'custom';

export interface DateRangeValue {
  preset: RangePreset;
  /** yyyy-MM-dd, only for custom */
  from?: string;
  to?: string;
}

export const RANGE_PRESETS: { id: Exclude<RangePreset, 'custom'>; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'last_7_days', label: 'Last 7 days' },
  { id: 'this_week', label: 'This week' },
  { id: 'last_week', label: 'Last week' },
  { id: 'last_30_days', label: 'Last 30 days' },
  { id: 'this_month', label: 'This month' },
  { id: 'last_month', label: 'Last month' },
  { id: 'all', label: 'All time' },
];

const LEGACY: Record<string, RangePreset> = {
  '7days': 'last_7_days',
  '3days': 'last_7_days',
  '14days': 'last_30_days',
  '30days': 'last_30_days',
  all_time: 'all',
};

export function normalizePreset(
  value: string | null | undefined,
  fallback: RangePreset = 'last_7_days',
): RangePreset {
  if (!value) return fallback;
  if (LEGACY[value]) return LEGACY[value];
  if (value === 'custom' || RANGE_PRESETS.some((p) => p.id === value)) return value as RangePreset;
  return fallback;
}

const WEEK = { weekStartsOn: 1 as const };

/** Resolve a range to concrete local-time bounds. `null` bounds mean unbounded. */
export function resolveRange(
  range: DateRangeValue,
  now = new Date(),
): { start: Date | null; end: Date | null } {
  switch (range.preset) {
    case 'today':
      return { start: startOfDay(now), end: endOfDay(now) };
    case 'yesterday': {
      const y = subDays(now, 1);
      return { start: startOfDay(y), end: endOfDay(y) };
    }
    case 'last_7_days':
      return { start: startOfDay(subDays(now, 6)), end: endOfDay(now) };
    case 'this_week':
      return { start: startOfWeek(now, WEEK), end: endOfDay(now) };
    case 'last_week': {
      const w = subWeeks(now, 1);
      return { start: startOfWeek(w, WEEK), end: endOfWeek(w, WEEK) };
    }
    case 'last_30_days':
      return { start: startOfDay(subDays(now, 29)), end: endOfDay(now) };
    case 'this_month':
      return { start: startOfMonth(now), end: endOfDay(now) };
    case 'last_month': {
      const m = subMonths(now, 1);
      return { start: startOfMonth(m), end: endOfMonth(m) };
    }
    case 'custom': {
      const from = range.from ? parseISO(range.from) : null;
      const to = range.to ? parseISO(range.to) : null;
      return {
        start: from && isValid(from) ? startOfDay(from) : null,
        end: to && isValid(to) ? endOfDay(to) : null,
      };
    }
    case 'all':
    default:
      return { start: null, end: null };
  }
}

/** RPCs that require explicit bounds get a wide window for "All time". */
export function resolveRangeBounded(range: DateRangeValue, now = new Date()): { start: Date; end: Date } {
  const { start, end } = resolveRange(range, now);
  return { start: start ?? new Date('2020-01-01T00:00:00Z'), end: end ?? endOfDay(now) };
}

export function rangeLabel(range: DateRangeValue): string {
  if (range.preset === 'custom') {
    const from = range.from ? parseISO(range.from) : null;
    const to = range.to ? parseISO(range.to) : null;
    if (from && to && isValid(from) && isValid(to)) {
      if (range.from === range.to) return format(from, 'd MMM yyyy');
      const sameYear = from.getFullYear() === to.getFullYear();
      return `${format(from, sameYear ? 'd MMM' : 'd MMM yyyy')} – ${format(to, 'd MMM yyyy')}`;
    }
    return 'Custom range';
  }
  return RANGE_PRESETS.find((p) => p.id === range.preset)?.label ?? 'Last 7 days';
}

/** A range key that is stable for query caching. */
export function rangeKey(range: DateRangeValue): string {
  return range.preset === 'custom' ? `custom:${range.from ?? ''}:${range.to ?? ''}` : range.preset;
}

/**
 * Timestamps come back from Postgres in several shapes ("2026-03-01 10:00:00",
 * with or without a zone). Anything without a zone is UTC.
 */
export function parseDbDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  if (value instanceof Date) return isValid(value) ? value : null;
  const normalized = String(value).trim().replace(' ', 'T');
  const hasZone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(normalized);
  const date = new Date(hasZone || normalized.length <= 10 ? normalized : `${normalized}Z`);
  return isValid(date) ? date : null;
}

export const toDateInput = (d: Date) => format(d, 'yyyy-MM-dd');

/** The browser's IANA zone; report days and hours use it, like the date filters do. */
export function viewerTimeZone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
}
